import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Users, Search, Calendar, Award, ChevronRight } from 'lucide-react';
import { companionApi } from '../services/api';
import { Input } from '../components/ui/input';
import { SectionHeader, PageLoading, AnimatedCard } from '../components/shared';

const Companions = () => {
  const { t } = useTranslation();
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCompanions = async () => {
      try {
        const response = await companionApi.getCompanions(0, 100, searchQuery || undefined);
        setCompanions(response.data);
      } catch (error) {
        console.error('Error fetching companions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchCompanions, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  if (loading && companions.length === 0) return <PageLoading />;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div 
        className="relative py-20 px-4 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1770339030328-d8b7c76adff1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxlbWVyYWxkJTIwZ3JlZW4lMjBhYnN0cmFjdCUyMHRleHR1cmV8ZW58MHx8fHwxNzc1NDY5MDYxfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span className="overline mb-4 block">Nobles Compagnons</span>
          <h1 className="font-arabic text-4xl sm:text-5xl text-primary mb-4" dir="rtl">
            الصحابة
          </h1>
          <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-4">
            Les Compagnons
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Découvrez les nobles compagnons du Prophète Mohammed ﷺ, leurs vies,
            leurs contributions et les hadiths qui leur sont attribués.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              placeholder={t('companions.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-surface border-border"
              data-testid="companion-search-input"
            />
          </div>
          <Link
            to="/companions/stories"
            className="text-sm text-primary hover:underline whitespace-nowrap"
            data-testid="companions-stories-link"
          >
            Histoires des compagnons →
          </Link>
        </div>

        {/* Companions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {companions.map((companion, index) => (
            <Link 
              key={companion.id} 
              to={`/companions/${companion.id}`}
              data-testid={`companion-card-${companion.id}`}
            >
              <AnimatedCard 
                index={index}
                className="h-full hover:scale-[1.02] cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-secondary/30 flex items-center justify-center flex-shrink-0">
                    <span className="font-arabic text-xl text-primary">
                      {companion.name_arabic.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg text-text-primary group-hover:text-primary transition-colors">
                      {companion.name_french || companion.name_english}
                    </h3>
                    <p className="font-arabic text-primary" dir="rtl">
                      {companion.name_arabic}
                    </p>
                    {companion.nickname && (
                      <p className="text-sm text-text-muted mt-1">{companion.nickname}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" />
                </div>
                
                <p className="text-sm text-text-secondary mt-4 line-clamp-2">
                  {companion.biography}
                </p>
                
                <div className="flex items-center gap-4 mt-4 text-xs text-text-muted">
                  {companion.birth_year && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {companion.birth_year} - {companion.death_year || '?'}
                    </span>
                  )}
                  {companion.notable_achievements?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {t('companions.achievements_count', { count: companion.notable_achievements.length })}
                    </span>
                  )}
                </div>
                
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-text-muted">
                    {companion.relationship_to_prophet}
                  </p>
                </div>
              </AnimatedCard>
            </Link>
          ))}
        </div>

        {companions.length === 0 && !loading && (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">{t('companions.no_companion_found')}</h3>
            <p className="text-text-secondary">{t('companions.try_other_search')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Companions;
